/*
  A JS reference is a set of group pages. A group page holds every method of
  its group, and each method is an anchor on that page
  (`/reference/cart-widget/methods#open`). The base path of the reference is
  the overview page.

  This module holds the shape both references share; each one lists its groups
  in its own `sections.ts`.
*/

export type Item = {
  /** Anchor on the group page, and the `id` of the <Method> block. */
  slug: string;
  /** Sidebar entry, in plain English, e.g. "Add an item". */
  label: string;
  /** Mono heading of the block, e.g. `subbly.cart.addItem(payload)`. */
  signature: string;
  /** One line about the method. Writers keep it next to the block they write. */
  description: string;
};

/*
  A topic is a section of the overview page that the sidebar lists after
  "Information", the way Stripe lists "Errors" and "Expanding responses" at the
  top of its API reference. The slug is the id Zudoku gives the `## Label`
  heading: the label in lower case, words joined with hyphens.
*/
export type Topic = {
  /** Anchor on the overview page, e.g. `expand-parameters`. */
  slug: string;
  /** Sidebar entry and the text of the heading, e.g. "Expand parameters". */
  label: string;
};

export type Group = {
  /** Last part of the page URL, e.g. `cart`. */
  slug: string;
  /** Sidebar entry and page heading, e.g. "Cart". */
  label: string;
  /** Lead paragraph and meta description of the page. */
  description: string;
  items: readonly Item[];
};

export type Reference = {
  /** Base path of the overview page, without a trailing slash. */
  path: string;
  /** Title of the reference; a group page title is built from it. */
  title: string;
  /** Meta description of the overview page. */
  description: string;
  /** Sections of the overview page, in page order. */
  topics: readonly Topic[];
  groups: readonly Group[];
  /** URL of a group page. */
  pathOf: (group: Group) => string;
  find: (slug: string) => Group | undefined;
};

export const item = (
  slug: string,
  label: string,
  signature: string,
  description: string,
): Item => ({ slug, label, signature, description });

/** A topic from its heading text; the slug is what Zudoku makes of it. */
export const topic = (label: string): Topic => ({
  slug: label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, ""),
  label,
});

export const group = (
  slug: string,
  label: string,
  description: string,
  items: readonly Item[],
): Group => ({ slug, label, description, items });

/** True when `pathname` is the base path itself or a page below it. */
export const isUnder = (pathname: string, base: string) =>
  pathname === base || pathname.startsWith(`${base}/`);

export const defineReference = (
  reference: Omit<Reference, "pathOf" | "find">,
): Reference => ({
  ...reference,
  pathOf: (group) => `${reference.path}/${group.slug}`,
  find: (slug) => reference.groups.find((group) => group.slug === slug),
});
